import React from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';

const ProcessFlow = () => {
  return (
    <section id="flow" className="py-20 bg-slate-50">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800 border-blue-200 font-semibold">Process Design</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Access Request Process Flow
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl">
              As-is vs to-be workflow redesign for IAM access requests at Client A.
              <br />
              <strong className="text-blue-600">Provisioning time: 14 days → 48 hours</strong> ·
              <strong className="text-teal-600"> Audit completeness: 40% → 100%</strong>
            </p>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-6 mb-12 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-4 h-4 bg-red-100 border-2 border-red-500"></div>
              <span className="text-slate-700 font-medium">As-is pain point</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-4 h-4 bg-green-100 border-2 border-green-600"></div>
              <span className="text-slate-700 font-medium">To-be automated step</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-4 h-4 bg-blue-100 border-2 border-blue-500 transform rotate-45"></div>
              <span className="text-slate-700 font-medium">Decision point</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-4 h-4 bg-amber-100 border-2 border-amber-500 rounded-full"></div>
              <span className="text-slate-700 font-medium">Notification</span>
            </div>
          </div>

          {/* Process Flow Diagram */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* As-Is Column */}
            <Card className="border-2 border-red-200 bg-white shadow-lg">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-red-700 mb-2">As-Is — avg 14 days</h3>
                  <div className="w-20 h-1 bg-red-500 mx-auto"></div>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-50 border border-slate-300 rounded p-4">
                    <p className="font-medium text-slate-800">Employee emails manager</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-slate-50 border border-slate-300 rounded p-4">
                    <p className="font-medium text-slate-800">Manager emails IT team</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-red-50 border-2 border-red-400 rounded p-4">
                    <p className="font-medium text-red-900">⚠️ Manual ticket created by IT</p>
                    <p className="text-sm text-red-700 mt-1">Avg 2 days delay</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-red-50 border-2 border-red-400 rounded p-4">
                    <p className="font-medium text-red-900">⚠️ Sequential approvals</p>
                    <p className="text-sm text-red-700 mt-1">Mgr → Security → IT → Provision</p>
                    <p className="text-sm text-red-700">No SLA · 10–14 days</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-red-50 border-2 border-red-400 rounded p-4">
                    <p className="font-medium text-red-900">⚠️ Email-only approvals</p>
                    <p className="text-sm text-red-700 mt-1">No audit trail · No escalation</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* To-Be Column */}
            <Card className="border-2 border-green-200 bg-white shadow-lg">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-green-700 mb-2">To-Be — target 48 hours</h3>
                  <div className="w-20 h-1 bg-green-500 mx-auto"></div>
                </div>

                <div className="space-y-4">
                  <div className="bg-green-50 border-2 border-green-500 rounded p-4">
                    <p className="font-medium text-green-900">✓ Employee submits Saviynt portal</p>
                    <p className="text-sm text-green-700 mt-1">Self-service</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-green-50 border-2 border-green-500 rounded p-4">
                    <p className="font-medium text-green-900">✓ Auto-ticket &lt; 5 min</p>
                    <p className="text-sm text-green-700 mt-1">Jira ticket auto-created + classified</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-blue-50 border-2 border-blue-500 rounded p-4">
                    <p className="font-medium text-blue-900 text-center">◆ P1 priority access?</p>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                      <span className="text-blue-700 text-center font-medium">4h SLA</span>
                      <span className="text-blue-700 text-center font-medium">48h SLA</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-green-50 border-2 border-green-500 rounded p-4">
                    <p className="font-medium text-green-900">✓ Parallel approvals</p>
                    <p className="text-sm text-green-700 mt-1">Manager + Security simultaneously</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-amber-50 border-2 border-amber-500 rounded-full p-4">
                    <p className="font-medium text-amber-900 text-center text-sm">Auto-escalation at 24h</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>

                  <div className="bg-green-50 border-2 border-green-500 rounded p-4">
                    <p className="font-medium text-green-900">✓ Auto-provision + audit trail</p>
                    <p className="text-sm text-green-700 mt-1">100% audit completeness</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Outcome Summary */}
          <Card className="mt-8 border-2 border-blue-200 bg-white shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Outcome</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-blue-600 mb-2">14d → 48h</p>
                  <p className="text-sm text-slate-600 font-medium">Provisioning time</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-teal-600 mb-2">40% → 100%</p>
                  <p className="text-sm text-slate-600 font-medium">Audit completeness</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-indigo-600 mb-2">–35%</p>
                  <p className="text-sm text-slate-600 font-medium">Requirement ambiguity</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ProcessFlow;
